// CREW-1295 — what a slot on the board still lacks at its current stage.
//
// "Which slot needs me?" used to mean opening every agreed slot one by one.
// Each rule below names the data a slot must carry once it reaches a status;
// `slotProblems` returns the ones missing, and the board draws an info button
// on any slot whose list is not empty (`habrahabr` in component/index.jsx).
//
// Kept out of the selector so the rules read — and test — as a table.

import { tryParseDate } from '../../utils/DateTime';

// The checks apply from 1 September 2026, when slot wages and travel dates came
// into use (CREW-1184): to every slot that date crosses and every later one —
// in short, a slot that ends on or after it. Anything that ended earlier
// predates the fields, and flagging it would mark history nobody is going to
// fill in. The slot form's wage check follows the same line.
export const CHECKS_START = '2026-09-01';

// The calendar day as `yyyy-MM-dd`, whatever shape the date arrives in: a bare
// day from the API, a datetime with the server's offset, or the Date a picker
// leaves in the form. `tryParseDate` keeps the day the server meant.
const dayKey = (value) => {
  const day = tryParseDate(value);
  if (!day || Number.isNaN(day.getTime())) return null;

  const month = String(day.getMonth() + 1).padStart(2, '0');
  const date = String(day.getDate()).padStart(2, '0');

  return `${day.getFullYear()}-${month}-${date}`;
};

// Does the slot fall under the checks at all? A slot with no end cannot say,
// and saying nothing is the safe side.
export const isCheckedSlot = ({ end }) => {
  const key = dayKey(end);

  return key !== null && key >= CHECKS_START;
};

// Travel start is the journey OUT, made before the seafarer signs on. A slot
// that signed on before 1 September 2026 crosses the line but flew before
// travel dates existed — there is nothing to enter, so it is not asked for.
const signedOnFromLine = ({ start }) => {
  const key = dayKey(start);

  return key !== null && key >= CHECKS_START;
};

// From `approved` the terms are agreed and frozen onto the slot (CREW-1184),
// so a wage that adds up to nothing is a contract with no pay on it. The slot
// form checks the same statuses (Form/component/TimeplanFields.jsx).
export const WAGES_REQUIRED_STATUSES = ['approved', 'on_board', 'completed'];

// The owner pays for no journey: the travel fields are hidden on such a slot
// (CREW-1183), so an empty date there is not a gap. Absent reads as paid — the
// same default the slot form uses.
const travelPaid = (slot) => slot.travel_payment_included !== false;

// `wages_total` is a decimal STRING ("0.0", "13200.0" — how Rails serialises a
// BigDecimal), or null when the slot has no wage row, which is the same gap as
// a zero. Absent means an older API that cannot tell us, and saying nothing is
// how the board behaved before.
const wagesKnown = (slot) => slot.wages_total !== undefined;
const wagesEmpty = (slot) => !(Number(slot.wages_total) > 0);

export const SLOT_PROBLEM_RULES = [
  {
    key: 'travel_start',
    statuses: ['on_board'],
    applies: (slot) => travelPaid(slot) && signedOnFromLine(slot),
    isMissing: (slot) => !slot.travel_start,
    title: 'Travel start is not set',
    detail: 'The seafarer is on board, but the journey out has no date.',
  },
  {
    key: 'wages_total',
    statuses: WAGES_REQUIRED_STATUSES,
    applies: wagesKnown,
    isMissing: wagesEmpty,
    title: 'Total wages is 0',
    detail: 'Nothing is filled in the contract wages of this slot.',
  },
  {
    key: 'travel_end',
    statuses: ['completed'],
    applies: travelPaid,
    isMissing: (slot) => !slot.travel_end,
    title: 'Travel end is not set',
    detail: 'The contract is completed, but the journey home has no date.',
  },
];

/**
 * slotProblems
 * @param {Object} slot - a timeplan as the board holds it (snake_case keys)
 * @returns {Array<{key: string, title: string, detail: string}>} in rule order; empty when nothing
 *   is missing, or when the slot ended before the checks start
 */
export const slotProblems = (slot) => {
  if (!isCheckedSlot(slot)) return [];

  return SLOT_PROBLEM_RULES
    .filter((rule) => rule.statuses.includes(slot.status) && rule.applies(slot) && rule.isMissing(slot))
    .map(({ key, title, detail }) => ({ key, title, detail }));
};
